import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { Divider, Text } from "@src/appV2/redesign/system/ui-components";
import { formatHoursDurationCompact } from "@src/appV2/redesign/utils/formatHoursDurationCompact";

import { type TimesheetEntry } from "../hooks/useTimesheetEntry";
import { calculateTimesheetWorkedHours } from "../utils/timesheetFormatUtils";

interface TimesheetTotalsFooterProps {
  displayTimesheet: TimesheetEntry;
  /**
   * Pass the original shift timesheet (hcp_clock_in/out) to show a Verified/Pending
   * hours breakdown when the worker is claiming more time than recorded.
   */
  verifiedTimesheet?: TimesheetEntry;
  /**
   * Options forwarded to formatHoursDurationCompact.
   * Defaults to { areLeadingZeroesAllowed: false, omitZeroMinutes: true } (compact/editable style).
   * Pass { areLeadingZeroesAllowed: true, omitZeroMinutes: false } to restore the read-only style
   * that shows "8h 00m" rather than "8h".
   */
  formatOptions?: { areLeadingZeroesAllowed?: boolean; omitZeroMinutes?: boolean };
  /** Pre-formatted dollar string (e.g. "$281.84"). Renders a "Paid out via InstantPay" row when defined. */
  paidOutAmount?: string;
  /** Pre-formatted dollar string (e.g. "+$2.84"). Renders a "Pending payment" row in pending tone when defined. */
  pendingPaymentAmount?: string;
}

interface TotalsRowProps {
  label: string;
  value: string;
  tone?: "default" | "pending";
}

function TotalsRow(props: TotalsRowProps) {
  const { label, value, tone = "default" } = props;
  const isPending = tone === "pending";

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box component="dt">
        <Text bold sx={{ color: (theme) => theme.palette.text.tertiary }}>
          {label}
        </Text>
      </Box>
      <Box component="dd" sx={{ margin: 0 }}>
        <Text
          bold
          variant="h5"
          sx={{
            color: (theme) =>
              isPending ? theme.palette.intent?.warning?.text : theme.palette.text.primary,
          }}
        >
          {value}
        </Text>
      </Box>
    </Stack>
  );
}

interface TotalsBreakdownRow {
  label: string;
  value: string;
}

interface TotalsBreakdownProps {
  primary?: TotalsBreakdownRow;
  pending?: TotalsBreakdownRow;
}

function TotalsBreakdown(props: TotalsBreakdownProps) {
  const { primary, pending } = props;

  return (
    <Stack component="dl" spacing={2} sx={{ margin: 0 }}>
      {isDefined(primary) && <TotalsRow label={primary.label} value={primary.value} />}
      {isDefined(pending) && (
        <TotalsRow label={pending.label} value={pending.value} tone="pending" />
      )}
    </Stack>
  );
}

export function TimesheetTotalsFooter(props: TimesheetTotalsFooterProps) {
  const {
    displayTimesheet,
    verifiedTimesheet,
    formatOptions,
    paidOutAmount,
    pendingPaymentAmount,
  } = props;

  const adjustedHours = calculateTimesheetWorkedHours(displayTimesheet);
  const verifiedHours = isDefined(verifiedTimesheet)
    ? calculateTimesheetWorkedHours(verifiedTimesheet)
    : undefined;
  const pendingHours =
    isDefined(verifiedHours) && adjustedHours > verifiedHours
      ? adjustedHours - verifiedHours
      : undefined;

  const hasHoursBreakdown = isDefined(verifiedHours) && isDefined(pendingHours);
  const hasPaymentBreakdown = isDefined(paidOutAmount) || isDefined(pendingPaymentAmount);

  if (hasHoursBreakdown || hasPaymentBreakdown) {
    // Use a consistent "Xh Ym" format for both verified and pending values so that
    // the breakdown reads the same across editable and read-only timesheet screens.
    const breakdownFormatOptions = { areLeadingZeroesAllowed: true, omitZeroMinutes: false };

    return (
      <Stack spacing={4}>
        {hasHoursBreakdown && isDefined(pendingHours) && (
          <TotalsBreakdown
            primary={{
              label: "Verified hours",
              value: formatWorkedHours(verifiedHours, breakdownFormatOptions),
            }}
            pending={{
              label: "Pending approval",
              value: `+${formatWorkedHours(pendingHours, breakdownFormatOptions)}`,
            }}
          />
        )}
        {hasHoursBreakdown && hasPaymentBreakdown && <Divider />}
        {hasPaymentBreakdown && (
          <TotalsBreakdown
            primary={
              isDefined(paidOutAmount)
                ? { label: "Paid out via InstantPay", value: paidOutAmount }
                : undefined
            }
            pending={
              isDefined(pendingPaymentAmount)
                ? { label: "Pending payment", value: pendingPaymentAmount }
                : undefined
            }
          />
        )}
      </Stack>
    );
  }

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Text bold variant="h5" sx={{ color: (theme) => theme.palette.text.tertiary }}>
        Total
      </Text>
      <Text bold variant="h5" sx={{ color: (theme) => theme.palette.success.main }}>
        {formatWorkedHours(adjustedHours, formatOptions)}
      </Text>
    </Stack>
  );
}

function formatWorkedHours(
  hours: number,
  options?: { areLeadingZeroesAllowed?: boolean; omitZeroMinutes?: boolean }
): string {
  return formatHoursDurationCompact(
    hours,
    options ?? { areLeadingZeroesAllowed: false, omitZeroMinutes: true }
  );
}
